import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useSession } from '@/contexts/SessionContext';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  User, Users, MapPin, GraduationCap, Heart, Bus, CreditCard,
  FileText, Upload, CheckCircle, AlertCircle, Save, ArrowLeft, ArrowRight, Camera, X, Loader2
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const RELIGIONS = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Other'];
const CATEGORIES = ['General', 'SC', 'ST', 'OBC', 'EWS'];

// Indian States
const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh'
];

// Districts by State (major districts)
const DISTRICTS_BY_STATE = {
  'West Bengal': ['Kolkata', 'Howrah', 'North 24 Parganas', 'South 24 Parganas', 'Hooghly', 'Nadia', 'Murshidabad', 'Bardhaman', 'Birbhum', 'Bankura', 'Purulia', 'Medinipur East', 'Medinipur West', 'Jalpaiguri', 'Darjeeling', 'Cooch Behar', 'Malda', 'Dinajpur North', 'Dinajpur South'],
  'Bihar': ['Patna', 'Gaya', 'Muzaffarpur', 'Bhagalpur', 'Darbhanga', 'Purnia', 'Nalanda', 'Vaishali', 'Begusarai', 'Samastipur', 'Munger', 'Buxar', 'Katihar', 'Madhubani'],
  'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Hazaribagh', 'Deoghar', 'Giridih', 'Dumka', 'Ramgarh'],
  'Delhi': ['Central Delhi', 'East Delhi', 'New Delhi', 'North Delhi', 'North East Delhi', 'North West Delhi', 'Shahdara', 'South Delhi', 'South East Delhi', 'South West Delhi', 'West Delhi'],
  'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kolhapur', 'Sangli', 'Satara'],
  'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Varanasi', 'Agra', 'Prayagraj', 'Meerut', 'Noida', 'Ghaziabad', 'Gorakhpur', 'Mathura'],
  'Other': ['Other']
};

// Occupations
const OCCUPATIONS = [
  'Government Employee', 'Private Employee', 'Business Owner', 'Self Employed',
  'Doctor', 'Engineer', 'Teacher/Professor', 'Lawyer', 'Farmer', 'Retired',
  'Homemaker', 'Daily Wage Worker', 'Driver', 'Shopkeeper', 'Contractor',
  'Bank Employee', 'Police/Army/Defense', 'Politician', 'Not Employed', 'Other'
];

// Qualifications
const QUALIFICATIONS = [
  'Below 10th', '10th Pass', '12th Pass', 'Diploma', 'Graduate (BA/BSc/BCom)',
  'Post Graduate (MA/MSc/MCom)', 'B.Tech/B.E.', 'M.Tech/M.E.', 'MBBS/MD',
  'PhD', 'CA/CS', 'LLB/LLM', 'MBA', 'Other Professional Degree', 'Illiterate'
];

// Relations
const RELATIONS = [
  'Father', 'Mother', 'Guardian', 'Grand Father', 'Grand Mother',
  'Uncle', 'Aunt', 'Brother', 'Sister', 'Other'
];

const DOCUMENT_TYPES = [
  { key: 'student_photo', label: 'Student Photo', required: true, accept: 'image/*' },
  { key: 'birth_certificate', label: 'Birth Certificate', required: true, accept: '.pdf,image/*' },
  { key: 'aadhaar_student', label: 'Student Aadhaar Card', required: true, accept: '.pdf,image/*' },
  { key: 'previous_marksheet', label: 'Previous Marksheet', required: false, accept: '.pdf,image/*' },
  { key: 'transfer_certificate', label: 'Transfer Certificate (TC)', required: false, accept: '.pdf,image/*' },
  { key: 'character_certificate', label: 'Character Certificate', required: false, accept: '.pdf,image/*' },
  { key: 'father_aadhaar', label: "Father's Aadhaar", required: false, accept: '.pdf,image/*' },
  { key: 'mother_aadhaar', label: "Mother's Aadhaar", required: false, accept: '.pdf,image/*' },
  { key: 'father_photo', label: "Father's Photo", required: false, accept: 'image/*' },
  { key: 'mother_photo', label: "Mother's Photo", required: false, accept: 'image/*' },
  { key: 'caste_certificate', label: 'Caste Certificate', required: false, accept: '.pdf,image/*' },
  { key: 'income_certificate', label: 'Income Certificate', required: false, accept: '.pdf,image/*' },
  { key: 'address_proof', label: 'Address Proof', required: false, accept: '.pdf,image/*' },
];

const AdmissionFormPage = ({ editMode = false }) => {
  const { getAuthHeader } = useAuth();
  const { currentSession, checkSessionRequired } = useSession();
  const navigate = useNavigate();
  const { studentId } = useParams();
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [fetchingStudent, setFetchingStudent] = useState(editMode);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [allSections, setAllSections] = useState([]);  // All sections from API
  const [savedStudentId, setSavedStudentId] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [feeCategories, setFeeCategories] = useState([]);  // Student fee categories from system
  
  // Transport & Hostel states
  const [transportRoutes, setTransportRoutes] = useState([]);
  const [transportStops, setTransportStops] = useState([]);
  const [hostelBuildings, setHostelBuildings] = useState([]);
  const [hostelRooms, setHostelRooms] = useState([]);
  const [availableBeds, setAvailableBeds] = useState([]);
  const [transportFeePreview, setTransportFeePreview] = useState(null);
  const [hostelFeePreview, setHostelFeePreview] = useState(null);
  
  // Photo upload state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    date_of_birth: '',
    gender: '',
    blood_group: '',
    religion: '',
    category: 'General',
    nationality: 'Indian',
    aadhaar_number: '',
    place_of_birth: '',
    mother_tongue: '',
    
    // Student Contact (own email/phone for older students)
    email: '',
    phone: '',
    
    // Address
    present_address: '',
    permanent_address: '',
    city: '',
    district: '',
    state: 'West Bengal',
    pin_code: '',
    same_address: false,
    
    // Parent Info
    father_name: '',
    father_occupation: '',
    father_qualification: '',
    father_mobile: '',
    father_email: '',
    father_aadhaar: '',
    father_annual_income: '',
    mother_name: '',
    mother_occupation: '',
    mother_mobile: '',
    mother_aadhaar: '',
    guardian_name: '',
    guardian_relation: '',
    guardian_phone: '',
    guardian_occupation: '',
    
    // Academic
    class_id: '',
    section_id: '',
    admission_date: new Date().toISOString().split('T')[0],
    admission_type: 'new',
    previous_school_name: '',
    previous_school_board: '',
    previous_class: '',
    previous_percentage: '',
    previous_tc_number: '',
    
    // Medical
    medical_conditions: '',
    allergies: '',
    disabilities: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    
    // Transport & Hostel
    transport_required: false,
    pickup_point: '',
    transport_route_id: '',
    transport_stop_id: '',
    transport_type: 'both', // pickup, drop, both
    transport_payment_plan: 'monthly', // monthly, yearly
    hostel_required: false,
    hostel_building_id: '',
    hostel_room_id: '',
    hostel_bed_number: '',
    mess_type: 'veg', // veg, non_veg, special
    hostel_payment_plan: 'monthly', // monthly, yearly
    
    // Fee
    fee_category: 'general',
    scholarship_applied: false,
    scholarship_type: '',
  });

  useEffect(() => {
    fetchClasses();
  }, []);

  // Fetch student data in edit mode after classes are loaded
  useEffect(() => {
    const loadStudentData = async () => {
      if (!editMode || !studentId || classes.length === 0) return;
      
      try {
        setFetchingStudent(true);
        const res = await axios.get(`${API_URL}/api/students/${studentId}`, { headers: getAuthHeader() });
        const student = res.data;
        
        // Load sections for the student's class FIRST
        if (student.class_id) {
          const classSections = allSections.filter(s => s.class_id === student.class_id);
          setSections(classSections);
        }
        
        // Map student data to form
        setFormData({
          name: student.name || '',
          date_of_birth: student.date_of_birth || '',
          gender: student.gender || '',
          blood_group: student.blood_group || '',
          religion: student.religion || '',
          category: student.category || 'General',
          nationality: student.nationality || 'Indian',
          aadhaar_number: student.aadhaar_number || '',
          place_of_birth: student.place_of_birth || '',
          mother_tongue: student.mother_tongue || '',
          email: student.email || '',
          phone: student.phone || '',
          present_address: student.present_address || student.address || '',
          permanent_address: student.permanent_address || '',
          city: student.city || '',
          district: student.district || '',
          state: student.state || 'West Bengal',
          pin_code: student.pin_code || '',
          father_name: student.father_name || '',
          father_occupation: student.father_occupation || '',
          father_qualification: student.father_qualification || '',
          father_mobile: student.father_mobile || '',
          father_email: student.father_email || '',
          father_aadhaar: student.father_aadhaar || '',
          father_annual_income: student.father_annual_income || '',
          mother_name: student.mother_name || '',
          mother_occupation: student.mother_occupation || '',
          mother_mobile: student.mother_mobile || '',
          mother_aadhaar: student.mother_aadhaar || '',
          guardian_name: student.guardian_name || '',
          guardian_relation: student.guardian_relation || '',
          guardian_phone: student.guardian_phone || '',
          guardian_occupation: student.guardian_occupation || '',
          class_id: student.class_id || '',
          section_id: student.section_id || '',
          admission_date: student.admission_date || '',
          admission_type: student.admission_type || 'new',
          previous_school_name: student.previous_school_name || '',
          previous_school_board: student.previous_school_board || '',
          previous_class: student.previous_class || '',
          previous_percentage: student.previous_percentage || '',
          previous_tc_number: student.previous_tc_number || '',
          medical_conditions: student.medical_conditions || '',
          allergies: student.allergies || '',
          disabilities: student.disabilities || '',
          emergency_contact_name: student.emergency_contact_name || '',
          emergency_contact_phone: student.emergency_contact_phone || '',
          transport_required: student.transport_required || false,
          pickup_point: student.pickup_point || '',
          hostel_required: student.hostel_required || false,
          fee_category: student.fee_category || 'general',
          scholarship_applied: student.scholarship_applied || false,
          scholarship_type: student.scholarship_type || '',
        });
        
        setSavedStudentId(studentId);
        
        // Fetch existing photo
        fetchStudentPhoto(studentId);
      } catch (error) {
        toast.error('Failed to fetch student data');
        console.error(error);
      } finally {
        setFetchingStudent(false);
      }
    };
    
    loadStudentData();
  }, [editMode, studentId, classes, allSections]);

  // Photo handling functions
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (100 KB max)
      if (file.size > 100 * 1024) {
        toast.error('Photo size must be less than 100 KB');
        e.target.value = '';
        return;
      }
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        e.target.value = '';
        return;
      }
      
      // Show preview immediately
      setPhotoPreview(URL.createObjectURL(file));
      
      // If editing existing student, upload immediately
      if (editMode && studentId) {
        setPhotoUploading(true);
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('file', file);
          uploadFormData.append('document_type', 'student_photo');
          
          await axios.post(
            `${API_URL}/api/documents/upload/${studentId}`,
            uploadFormData,
            { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
          );
          toast.success('Photo uploaded successfully!');
        } catch (error) {
          toast.error('Photo upload failed');
          setPhotoPreview(null);
        } finally {
          setPhotoUploading(false);
        }
      } else {
        // For new student, store file to upload after save
        setPhotoFile(file);
      }
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  // Fetch existing photo for edit mode
  const fetchStudentPhoto = async (sid) => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/student/${sid}`, { headers: getAuthHeader() });
      const docs = res.data.documents || [];
      const photos = docs.filter(d => d.document_type === 'student_photo')
        .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
      const photoDoc = photos[0];
      if (photoDoc?.cloudinary_url) {
        setPhotoPreview(photoDoc.cloudinary_url);
      } else if (photoDoc?.file_path && photoDoc.file_path.startsWith('http')) {
        setPhotoPreview(photoDoc.file_path);
      }
    } catch (error) {
      console.error('Failed to fetch photo:', error);
    }
  };

  // Upload photo after student is saved (for new students)
  const uploadStudentPhoto = async (sid) => {
    if (!photoFile) return;
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', photoFile);
      uploadFormData.append('document_type', 'student_photo');
      
      await axios.post(
        `${API_URL}/api/documents/upload/${sid}`,
        uploadFormData,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Photo uploaded!');
    } catch (error) {
      console.error('Photo upload failed:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      const [classRes, sectionRes] = await Promise.all([
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/sections`, { headers: getAuthHeader() })
      ]);
      // Handle paginated response
      const classesData = classRes.data.items || classRes.data;
      const sectionsData = sectionRes.data.items || sectionRes.data || [];
      setClasses(classesData);
      setAllSections(Array.isArray(sectionsData) ? sectionsData : []);
    } catch (error) {
      console.error('Failed to fetch classes');
    }
  };

  // Fetch Student Fee Categories from system
  const fetchFeeCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fee-management/student-categories`, { headers: getAuthHeader() });
      const categories = res.data || [];
      setFeeCategories(Array.isArray(categories) ? categories.filter(c => c.is_active) : []);
    } catch (error) {
      console.error('Failed to fetch fee categories');
      // Fallback to empty - UI will show message
      setFeeCategories([]);
    }
  };

  // Fetch Transport Routes
  const fetchTransportRoutes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/transport-v2/routes`, { headers: getAuthHeader() });
      // Handle paginated response
      const routes = res.data?.items || res.data || [];
      setTransportRoutes(Array.isArray(routes) ? routes : []);
    } catch (error) {
      console.error('Failed to fetch transport routes');
      setTransportRoutes([]);
    }
  };

  // Fetch Transport Stops for selected route
  const fetchTransportStops = async (routeId) => {
    if (!routeId) {
      setTransportStops([]);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/transport-v2/stops?route_id=${routeId}`, { headers: getAuthHeader() });
      // Handle paginated response
      const stops = res.data?.items || res.data || [];
      setTransportStops(Array.isArray(stops) ? stops : []);
    } catch (error) {
      console.error('Failed to fetch transport stops');
      setTransportStops([]);
    }
  };

  // Calculate Transport Fee based on stop
  const calculateTransportFee = async (stopId) => {
    if (!stopId) {
      setTransportFeePreview(null);
      return;
    }
    const stop = transportStops.find(s => s.id === stopId);
    const route = transportRoutes.find(r => r.id === formData.transport_route_id);
    
    if (stop) {
      const distance = stop.distance_from_school_km || stop.distance_from_school || 0;
      
      // Calculate fee: Use stop's fixed_fee, or route's per_km_fee * distance, or route's fixed_fee
      let fee = 0;
      if (stop.fixed_fee) {
        fee = stop.fixed_fee;
      } else if (route?.per_km_fee && distance > 0) {
        fee = route.per_km_fee * distance;
      } else if (route?.fixed_fee) {
        fee = route.fixed_fee;
      }
      
      setTransportFeePreview({
        stopName: stop.stop_name,
        distance: distance,
        monthlyFee: fee
      });
    }
  };

  // Fetch Hostel Buildings
  const fetchHostelBuildings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hostel/buildings`, { headers: getAuthHeader() });
      setHostelBuildings(res.data || []);
    } catch (error) {
      console.error('Failed to fetch hostel buildings');
    }
  };

  // Fetch Hostel Rooms for selected building
  const fetchHostelRooms = async (buildingId) => {
    if (!buildingId) {
      setHostelRooms([]);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/hostel/rooms?building_id=${buildingId}`, { headers: getAuthHeader() });
      const rooms = res.data || [];
      // Filter rooms with available beds (total_beds > occupied_beds)
      const availableRooms = rooms.filter(room => {
        const totalBeds = room.total_beds || room.capacity || 0;
        const occupiedBeds = room.occupied_beds || room.occupied || 0;
        return totalBeds > occupiedBeds;
      }).map(room => ({
        ...room,
        available_beds: room.available_beds || ((room.total_beds || room.capacity || 0) - (room.occupied_beds || room.occupied || 0)),
        monthly_fee: room.monthly_fee || room.monthly_rent || 0
      }));
      setHostelRooms(availableRooms);
    } catch (error) {
      console.error('Failed to fetch hostel rooms');
      setHostelRooms([]);
    }
  };

  // Get available beds for selected room
  const getAvailableBeds = (roomId) => {
    const room = hostelRooms.find(r => r.id === roomId);
    if (!room) {
      setAvailableBeds([]);
      return;
    }
    
    // Generate bed numbers based on total_beds and occupied beds
    const totalBeds = room.total_beds || room.capacity || 0;
    const occupiedBedsCount = room.occupied_beds || room.occupied || 0;
    const available = [];
    
    // Generate available bed numbers (1 to totalBeds, excluding occupied count)
    for (let i = 1; i <= totalBeds; i++) {
      if (i > occupiedBedsCount || totalBeds - occupiedBedsCount >= i) {
        available.push(i);
      }
    }
    
    // If occupied_beds is a number, just generate available bed numbers
    if (typeof occupiedBedsCount === 'number') {
      available.length = 0; // Clear array
      for (let i = 1; i <= (totalBeds - occupiedBedsCount); i++) {
        available.push(i);
      }
    }
    
    setAvailableBeds(available);
    
    // Calculate hostel fee preview
    calculateHostelFee(roomId);
  };

  // Calculate Hostel Fee
  const calculateHostelFee = async (roomId) => {
    const room = hostelRooms.find(r => r.id === roomId);
    if (!room) {
      setHostelFeePreview(null);
      return;
    }
    
    // Use monthly_rent or monthly_fee (different APIs use different names)
    const roomFee = room.monthly_rent || room.monthly_fee || 0;
    
    // Get hostel settings for security deposit months
    try {
      const [messRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/hostel/mess/settings`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/hostel/settings`, { headers: getAuthHeader() })
      ]);
      const messSettings = messRes.data || {};
      const hostelSettings = settingsRes.data || {};
      
      const messType = formData.mess_type || 'veg';
      let messFee = 0;
      if (messType === 'veg') messFee = messSettings.veg_monthly_fee || 0;
      else if (messType === 'non_veg') messFee = messSettings.non_veg_monthly_fee || 0;
      else if (messType === 'special') messFee = messSettings.special_diet_fee || 0;
      
      const depositMonths = hostelSettings.security_deposit_months || 2;
      const securityDeposit = roomFee * depositMonths;
      
      setHostelFeePreview({
        roomNumber: room.room_number,
        roomType: room.room_type,
        roomFee: roomFee,
        messFee: messFee,
        messType: messType,
        securityDeposit: securityDeposit,
        depositMonths: depositMonths,
        totalMonthly: roomFee + messFee
      });
    } catch (error) {
      // Fallback without settings - use default 2 months
      setHostelFeePreview({
        roomNumber: room.room_number,
        roomType: room.room_type,
        roomFee: roomFee,
        messFee: 0,
        messType: formData.mess_type,
        securityDeposit: roomFee * 2,
        depositMonths: 2,
        totalMonthly: roomFee
      });
    }
  };

  // Load transport and hostel data on mount
  useEffect(() => {
    fetchTransportRoutes();
    fetchHostelBuildings();
    fetchFeeCategories();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Handle same address checkbox
      if (field === 'same_address' && value) {
        updated.permanent_address = prev.present_address;
      }
      
      // Clear transport data when transport_required is unchecked
      if (field === 'transport_required' && !value) {
        updated.transport_route_id = '';
        updated.transport_stop_id = '';
        setTransportStops([]);
        setTransportFeePreview(null);
      }
      
      // Clear hostel data when hostel_required is unchecked
      if (field === 'hostel_required' && !value) {
        updated.hostel_building_id = '';
        updated.hostel_room_id = '';
        updated.hostel_bed_number = '';
        setHostelRooms([]);
        setAvailableBeds([]);
        setHostelFeePreview(null);
      }
      
      return updated;
    });
    
    // Set sections when class changes - filter from allSections by class_id
    if (field === 'class_id' && value) {
      const classSections = allSections.filter(s => s.class_id === value);
      setSections(classSections);
      // Reset section_id when class changes
      setFormData(prev => ({ ...prev, section_id: '' }));
    } else if (field === 'class_id' && !value) {
      setSections([]);
    }
    
    // Transport route selection - fetch stops
    if (field === 'transport_route_id' && value) {
      fetchTransportStops(value);
      setFormData(prev => ({ ...prev, transport_stop_id: '' }));
      setTransportFeePreview(null);
    }
    
    // Transport stop selection - calculate fee
    if (field === 'transport_stop_id' && value) {
      calculateTransportFee(value);
    }
    
    // Hostel building selection - fetch rooms
    if (field === 'hostel_building_id' && value) {
      fetchHostelRooms(value);
      setFormData(prev => ({ ...prev, hostel_room_id: '', hostel_bed_number: '' }));
      setAvailableBeds([]);
      setHostelFeePreview(null);
    }
    
    // Hostel room selection - get available beds and calculate fee
    if (field === 'hostel_room_id' && value) {
      getAvailableBeds(value);
      setFormData(prev => ({ ...prev, hostel_bed_number: '' }));
    }
    
    // Mess type change - recalculate hostel fee
    if (field === 'mess_type' && formData.hostel_room_id) {
      setTimeout(() => calculateHostelFee(formData.hostel_room_id), 100);
    }
  };

  const handleSaveStudent = async () => {
    if (!formData.name || !formData.date_of_birth || !formData.class_id) {
      toast.error('Please fill required fields: Name, Date of Birth, Class');
      return;
    }

    setLoading(true);
    try {
      // Clean formData: remove empty strings and convert to null for optional fields
      const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
        // Skip empty strings - they cause Pydantic validation errors for EmailStr fields
        if (value === '' || value === null || value === undefined) {
          return acc;
        }
        acc[key] = value;
        return acc;
      }, {});
      
      const payload = {
        ...cleanedData,
        father_annual_income: formData.father_annual_income ? parseFloat(formData.father_annual_income) : null,
        // Transport allocation data
        transport_allocation: formData.transport_required && formData.transport_route_id && formData.transport_stop_id ? {
          route_id: formData.transport_route_id,
          stop_id: formData.transport_stop_id,
          transport_type: formData.transport_type || 'both',
          payment_plan: formData.transport_payment_plan || 'monthly'
        } : null,
        // Hostel allocation data
        hostel_allocation: formData.hostel_required && formData.hostel_room_id && formData.hostel_bed_number ? {
          room_id: formData.hostel_room_id,
          bed_number: parseInt(formData.hostel_bed_number),
          mess_type: formData.mess_type || 'veg',
          payment_plan: formData.hostel_payment_plan || 'monthly'
        } : null
      };
      
      let res;
      if (editMode && studentId) {
        // Update existing student
        res = await axios.put(`${API_URL}/api/students/${studentId}`, payload, { headers: getAuthHeader() });
        toast.success('Student updated successfully!');
      } else {
        // Create new student with auto fee assignment and optional transport/hostel allocation
        res = await axios.post(`${API_URL}/api/students?auto_assign_fees=true`, payload, { headers: getAuthHeader() });
        setSavedStudentId(res.data.id);
        
        // Build success message
        let successMsg = `Student admitted! ID: ${res.data.student_id}`;
        const feesCount = res.data.fees_assigned?.length || 0;
        const allocations = [];
        
        if (res.data.transport_allocated) {
          allocations.push('Transport allocated');
        }
        if (res.data.hostel_allocated) {
          allocations.push('Hostel allocated');
        }
        if (feesCount > 0) {
          allocations.push(`${feesCount} fee items assigned`);
        }
        
        if (allocations.length > 0) {
          successMsg += `. ${allocations.join(', ')}.`;
        }
        
        toast.success(successMsg);
        
        // Upload photo for new student
        if (photoFile && res.data.id) {
          await uploadStudentPhoto(res.data.id);
        }
      }
      
      if (!editMode) {
        setActiveTab('documents');
      } else {
        navigate('/students');
      }
    } catch (error) {
      console.error('Save student error:', error.response?.data);
      // Handle Pydantic validation errors which come as array/object
      const errorData = error.response?.data?.detail;
      let errorMessage = 'Failed to save student';
      
      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (Array.isArray(errorData)) {
        // Pydantic validation errors
        errorMessage = errorData.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorData && typeof errorData === 'object') {
        errorMessage = errorData.msg || errorData.message || JSON.stringify(errorData);
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (docType, file) => {
    if (!savedStudentId) {
      toast.error('Please save student details first');
      return;
    }

    // File size validation - Photos: 100 KB, Documents: 300 KB
    const isPhoto = docType.includes('photo');
    const maxSize = isPhoto ? 100 * 1024 : 300 * 1024;
    const maxSizeLabel = isPhoto ? '100 KB' : '300 KB';
    
    if (file.size > maxSize) {
      toast.error(`File size too large! Maximum: ${maxSizeLabel}. Your file: ${(file.size / 1024).toFixed(1)} KB`);
      return;
    }

    setUploadingDoc(docType);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('document_type', docType);

    try {
      const res = await axios.post(
        `${API_URL}/api/documents/upload/${savedStudentId}`,
        formDataUpload,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      setUploadedDocs(prev => ({ ...prev, [docType]: res.data.filename }));
      toast.success('Document uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingDoc(null);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: User },
    { id: 'address', label: 'Address', icon: MapPin },
    { id: 'parent', label: 'Parent/Guardian', icon: Users },
    { id: 'academic', label: 'Academic', icon: GraduationCap },
    { id: 'medical', label: 'Medical', icon: Heart },
    { id: 'other', label: 'Transport & Fee', icon: Bus },
    { id: 'documents', label: 'Documents', icon: FileText },
  ];

  const currentTabIndex = tabs.findIndex(t => t.id === activeTab);
  const progress = ((currentTabIndex + 1) / tabs.length) * 100;

  // Check if session is set
  const sessionCheck = checkSessionRequired();

  if (fetchingStudent) {
    return (
      <DashboardLayout title={editMode ? "Edit Student" : "New Student Admission"}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Show session required message for new entries
  if (!editMode && !sessionCheck.allowed) {
    return (
      <DashboardLayout title="New Student Admission">
        <div className="max-w-2xl mx-auto mt-10">
          <Alert variant="destructive">
            <AlertCircle className="h-5 w-5" />
            <AlertTitle>Session Not Set!</AlertTitle>
            <AlertDescription className="mt-2">
              {sessionCheck.message}
            </AlertDescription>
          </Alert>
          <div className="flex gap-4 mt-6">
            <Button variant="outline" onClick={() => navigate('/students')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Students
            </Button>
            <Button onClick={() => navigate('/academic')}>
              <GraduationCap className="h-4 w-4 mr-2" /> Go to Academic Settings
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={editMode ? "Edit Student" : "New Student Admission"}>
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Back Button */}
        <Button variant="outline" onClick={() => navigate('/students')} className="mb-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Students
        </Button>

        {/* Progress */}
        <div className="flex items-center gap-4">
          <Progress value={progress} className="flex-1" />
          <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
        </div>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              {editMode ? 'Edit Student Details' : 'Student Admission Form'}
            </CardTitle>
            <CardDescription>
              {editMode ? 'Update student information' : 'Fill all required details for student admission'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-7 mb-6">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <TabsTrigger key={tab.id} value={tab.id} className="text-xs">
                      <Icon className="h-4 w-4 mr-1 hidden sm:inline" />
                      {tab.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {/* Basic Information */}
              <TabsContent value="basic" className="space-y-4">
                {/* Photo Upload Section */}
                <div className="flex items-start gap-6 p-4 bg-muted/50 rounded-lg mb-4">
                  <div className="relative">
                    <div className="h-32 w-32 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center overflow-hidden bg-background">
                      {photoPreview ? (
                        <img src={photoPreview} alt="Student" className="h-full w-full object-cover" />
                      ) : (
                        <div className="text-center p-2">
                          <Camera className="h-8 w-8 mx-auto text-muted-foreground/50" />
                          <p className="text-xs text-muted-foreground mt-1">Student Photo</p>
                        </div>
                      )}
                      {photoUploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <Loader2 className="h-6 w-6 animate-spin text-white" />
                        </div>
                      )}
                    </div>
                    {photoPreview && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6"
                        onClick={removePhoto}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <h4 className="font-medium">Student Photo</h4>
                    <p className="text-sm text-muted-foreground">Upload a passport size photo (max 100 KB)</p>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                      id="student-photo-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={photoUploading}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {photoPreview ? 'Change Photo' : 'Upload Photo'}
                    </Button>
                    <ul className="text-xs text-muted-foreground mt-2 space-y-1">
                      <li>• Passport size photo preferred</li>
                      <li>• Max size: 100 KB</li>
                      <li>• Formats: JPG, PNG, WebP</li>
                    </ul>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Student Name (Full) *</Label>
                    <Input value={formData.name} onChange={e => handleInputChange('name', e.target.value)} placeholder="Full name as per documents" />
                  </div>
                  <div>
                    <Label>Date of Birth *</Label>
                    <Input type="date" value={formData.date_of_birth} onChange={e => handleInputChange('date_of_birth', e.target.value)} />
                  </div>
                  <div>
                    <Label>Gender *</Label>
                    <Select value={formData.gender} onValueChange={v => handleInputChange('gender', v)}>
                      <SelectTrigger><SelectValue placeholder="Select gender" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Blood Group</Label>
                    <Select value={formData.blood_group} onValueChange={v => handleInputChange('blood_group', v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {BLOOD_GROUPS.map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Religion</Label>
                    <Select value={formData.religion} onValueChange={v => handleInputChange('religion', v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {RELIGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={formData.category} onValueChange={v => handleInputChange('category', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nationality</Label>
                    <Input value={formData.nationality} onChange={e => handleInputChange('nationality', e.target.value)} />
                  </div>
                  <div>
                    <Label>Aadhaar Number</Label>
                    <Input value={formData.aadhaar_number} onChange={e => handleInputChange('aadhaar_number', e.target.value)} placeholder="12 digit Aadhaar" maxLength={12} />
                  </div>
                  <div>
                    <Label>Place of Birth</Label>
                    <Input value={formData.place_of_birth} onChange={e => handleInputChange('place_of_birth', e.target.value)} />
                  </div>
                  <div>
                    <Label>Mother Tongue</Label>
                    <Input value={formData.mother_tongue} onChange={e => handleInputChange('mother_tongue', e.target.value)} placeholder="e.g., Bengali" />
                  </div>
                </div>
                
                {/* Student Contact Info */}
                <div className="p-4 border rounded-lg bg-blue-50/50 space-y-4 mt-4">
                  <h3 className="font-semibold text-blue-600">Student Contact (For older students)</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Student Email</Label>
                      <Input type="email" value={formData.email} onChange={e => handleInputChange('email', e.target.value)} placeholder="student@email.com" />
                    </div>
                    <div>
                      <Label>Student Phone (10 digits)</Label>
                      <Input 
                        type="tel"
                        value={formData.phone} 
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                          handleInputChange('phone', val);
                        }}
                        maxLength={10}
                        placeholder="10 digit mobile number"
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Address */}
              <TabsContent value="address" className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <Label>Present Address *</Label>
                    <Textarea value={formData.present_address} onChange={e => handleInputChange('present_address', e.target.value)} rows={2} placeholder="House no, Street, Area" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={formData.same_address} onCheckedChange={v => handleInputChange('same_address', v)} />
                    <Label className="text-sm">Permanent address same as present</Label>
                  </div>
                  <div>
                    <Label>Permanent Address</Label>
                    <Textarea value={formData.permanent_address} onChange={e => handleInputChange('permanent_address', e.target.value)} rows={2} disabled={formData.same_address} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>City/Village</Label>
                      <Input value={formData.city} onChange={e => handleInputChange('city', e.target.value)} />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Select value={formData.state} onValueChange={v => { handleInputChange('state', v); handleInputChange('district', ''); }}>
                        <SelectTrigger><SelectValue placeholder="Select State" /></SelectTrigger>
                        <SelectContent>
                          {STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>District</Label>
                      <Select value={formData.district} onValueChange={v => handleInputChange('district', v)} disabled={!formData.state}>
                        <SelectTrigger><SelectValue placeholder={formData.state ? "Select District" : "Select State first"} /></SelectTrigger>
                        <SelectContent>
                          {(DISTRICTS_BY_STATE[formData.state] || DISTRICTS_BY_STATE['Other'] || []).map(d => (
                            <SelectItem key={d} value={d}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>PIN Code</Label>
                      <Input 
                        type="tel"
                        value={formData.pin_code} 
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                          handleInputChange('pin_code', val);
                        }} 
                        maxLength={6}
                        placeholder="6 digit PIN"
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Parent/Guardian */}
              <TabsContent value="parent" className="space-y-6">
                {/* Father's Info */}
                <div className="p-4 border rounded-lg space-y-4">
                  <h3 className="font-semibold text-blue-600">Father&apos;s Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Father&apos;s Name *</Label>
                      <Input value={formData.father_name} onChange={e => handleInputChange('father_name', e.target.value)} />
                    </div>
                    <div>
                      <Label>Occupation</Label>
                      <Select value={formData.father_occupation} onValueChange={v => handleInputChange('father_occupation', v)}>
                        <SelectTrigger><SelectValue placeholder="Select Occupation" /></SelectTrigger>
                        <SelectContent>
                          {OCCUPATIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Qualification</Label>
                      <Select value={formData.father_qualification} onValueChange={v => handleInputChange('father_qualification', v)}>
                        <SelectTrigger><SelectValue placeholder="Select Qualification" /></SelectTrigger>
                        <SelectContent>
                          {QUALIFICATIONS.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Mobile Number * (10 digits)</Label>
                      <Input 
                        type="tel"
                        value={formData.father_mobile} 
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                          handleInputChange('father_mobile', val);
                        }}
                        maxLength={10}
                        placeholder="10 digit mobile number"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input type="email" value={formData.father_email} onChange={e => handleInputChange('father_email', e.target.value)} />
                    </div>
                    <div>
                      <Label>Aadhaar Number</Label>
                      <Input 
                        type="tel"
                        value={formData.father_aadhaar} 
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                          handleInputChange('father_aadhaar', val);
                        }}
                        maxLength={12}
                        placeholder="12 digit Aadhaar"
                      />
                    </div>
                    <div>
                      <Label>Annual Income (₹)</Label>
                      <Input 
                        type="tel"
                        value={formData.father_annual_income} 
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '');
                          handleInputChange('father_annual_income', val);
                        }}
                        placeholder="Annual income in rupees"
                      />
                    </div>
                  </div>
                </div>

                {/* Mother's Info */}
                <div className="p-4 border rounded-lg space-y-4">
                  <h3 className="font-semibold text-pink-600">Mother&apos;s Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Mother&apos;s Name *</Label>
                      <Input value={formData.mother_name} onChange={e => handleInputChange('mother_name', e.target.value)} />
                    </div>
                    <div>
                      <Label>Occupation</Label>
                      <Select value={formData.mother_occupation} onValueChange={v => handleInputChange('mother_occupation', v)}>
                        <SelectTrigger><SelectValue placeholder="Select Occupation" /></SelectTrigger>
                        <SelectContent>
                          {OCCUPATIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Mobile Number (10 digits)</Label>
                      <Input 
                        type="tel"
                        value={formData.mother_mobile} 
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                          handleInputChange('mother_mobile', val);
                        }}
                        maxLength={10}
                        placeholder="10 digit mobile number"
                      />
                    </div>
                    <div>
                      <Label>Aadhaar Number</Label>
                      <Input 
                        type="tel"
                        value={formData.mother_aadhaar} 
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                          handleInputChange('mother_aadhaar', val);
                        }}
                        maxLength={12}
                        placeholder="12 digit Aadhaar"
                      />
                    </div>
                  </div>
                </div>

                {/* Guardian Info */}
                <div className="p-4 border rounded-lg space-y-4">
                  <h3 className="font-semibold text-amber-600">Guardian Information (If different from parents)</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Guardian Name</Label>
                      <Input value={formData.guardian_name} onChange={e => handleInputChange('guardian_name', e.target.value)} />
                    </div>
                    <div>
                      <Label>Relation with Student</Label>
                      <Select value={formData.guardian_relation} onValueChange={v => handleInputChange('guardian_relation', v)}>
                        <SelectTrigger><SelectValue placeholder="Select Relation" /></SelectTrigger>
                        <SelectContent>
                          {RELATIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Phone (10 digits)</Label>
                      <Input 
                        type="tel"
                        value={formData.guardian_phone} 
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                          handleInputChange('guardian_phone', val);
                        }}
                        maxLength={10}
                        placeholder="10 digit mobile number"
                      />
                    </div>
                    <div>
                      <Label>Occupation</Label>
                      <Select value={formData.guardian_occupation} onValueChange={v => handleInputChange('guardian_occupation', v)}>
                        <SelectTrigger><SelectValue placeholder="Select Occupation" /></SelectTrigger>
                        <SelectContent>
                          {OCCUPATIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Academic */}
              <TabsContent value="academic" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Admission Class *</Label>
                    <Select value={formData.class_id} onValueChange={v => handleInputChange('class_id', v)}>
                      <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                      <SelectContent>
                        {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Section</Label>
                    <Select value={formData.section_id || ''} onValueChange={v => handleInputChange('section_id', v)} disabled={sections.length === 0}>
                      <SelectTrigger><SelectValue placeholder={sections.length === 0 ? "Select class first" : "Select section"} /></SelectTrigger>
                      <SelectContent>
                        {sections.length > 0 ? (
                          sections.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)
                        ) : (
                          <SelectItem value="none" disabled>No sections available</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Admission Date *</Label>
                    <Input type="date" value={formData.admission_date} onChange={e => handleInputChange('admission_date', e.target.value)} />
                  </div>
                  <div>
                    <Label>Admission Type</Label>
                    <Select value={formData.admission_type} onValueChange={v => handleInputChange('admission_type', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New Admission</SelectItem>
                        <SelectItem value="transfer">Transfer (From another school with TC)</SelectItem>
                        <SelectItem value="re_admission">Re-Admission (Previously studied here)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Previous School */}
                <div className="p-4 border rounded-lg space-y-4 mt-4">
                  <h3 className="font-semibold">Previous School Details (For Transfer Students)</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>School Name</Label>
                      <Input value={formData.previous_school_name} onChange={e => handleInputChange('previous_school_name', e.target.value)} />
                    </div>
                    <div>
                      <Label>Board</Label>
                      <Input value={formData.previous_school_board} onChange={e => handleInputChange('previous_school_board', e.target.value)} placeholder="e.g., WBBSE, CBSE" />
                    </div>
                    <div>
                      <Label>Previous Class</Label>
                      <Input value={formData.previous_class} onChange={e => handleInputChange('previous_class', e.target.value)} />
                    </div>
                    <div>
                      <Label>Percentage/Grade</Label>
                      <Input value={formData.previous_percentage} onChange={e => handleInputChange('previous_percentage', e.target.value)} />
                    </div>
                    <div>
                      <Label>TC Number</Label>
                      <Input value={formData.previous_tc_number} onChange={e => handleInputChange('previous_tc_number', e.target.value)} />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Medical */}
              <TabsContent value="medical" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Medical Conditions (if any)</Label>
                    <Textarea value={formData.medical_conditions} onChange={e => handleInputChange('medical_conditions', e.target.value)} placeholder="Any chronic illness, ongoing treatment..." rows={2} />
                  </div>
                  <div>
                    <Label>Allergies</Label>
                    <Input value={formData.allergies} onChange={e => handleInputChange('allergies', e.target.value)} placeholder="Food, medicine allergies" />
                  </div>
                  <div>
                    <Label>Disabilities (if any)</Label>
                    <Input value={formData.disabilities} onChange={e => handleInputChange('disabilities', e.target.value)} />
                  </div>
                  <div>
                    <Label>Emergency Contact Name</Label>
                    <Input value={formData.emergency_contact_name} onChange={e => handleInputChange('emergency_contact_name', e.target.value)} />
                  </div>
                  <div>
                    <Label>Emergency Contact Phone (10 digits)</Label>
                    <Input 
                      type="tel"
                      value={formData.emergency_contact_phone} 
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleInputChange('emergency_contact_phone', val);
                      }}
                      maxLength={10}
                      placeholder="10 digit mobile number"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Transport & Fee */}
              <TabsContent value="other" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Transport */}
                  <div className="p-4 border rounded-lg space-y-4">
                    <h3 className="font-semibold flex items-center gap-2"><Bus className="h-4 w-4" /> Transport</h3>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={formData.transport_required} onCheckedChange={v => handleInputChange('transport_required', v)} />
                      <Label>Transport Required</Label>
                    </div>
                    {formData.transport_required && (
                      <div className="space-y-3 pl-4 border-l-2 border-blue-200">
                        <div>
                          <Label>Select Route *</Label>
                          <Select value={formData.transport_route_id} onValueChange={v => handleInputChange('transport_route_id', v)}>
                            <SelectTrigger><SelectValue placeholder="Select transport route" /></SelectTrigger>
                            <SelectContent>
                              {transportRoutes.length === 0 ? (
                                <SelectItem value="none" disabled>No routes available</SelectItem>
                              ) : (
                                transportRoutes.map(r => (
                                  <SelectItem key={r.id} value={r.id}>{r.route_name} ({r.start_point} → {r.end_point})</SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {formData.transport_route_id && (
                          <div>
                            <Label>Select Stop *</Label>
                            <Select value={formData.transport_stop_id} onValueChange={v => handleInputChange('transport_stop_id', v)}>
                              <SelectTrigger><SelectValue placeholder="Select pickup/drop stop" /></SelectTrigger>
                              <SelectContent>
                                {transportStops.length === 0 ? (
                                  <SelectItem value="none" disabled>No stops for this route</SelectItem>
                                ) : (
                                  transportStops.map(s => (
                                    <SelectItem key={s.id} value={s.id}>{s.stop_name} ({s.distance_from_school_km || s.distance_from_school || 0} km)</SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {formData.transport_stop_id && (
                          <div>
                            <Label>Transport Type</Label>
                            <Select value={formData.transport_type} onValueChange={v => handleInputChange('transport_type', v)}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="both">Both (Pickup & Drop)</SelectItem>
                                <SelectItem value="pickup">Pickup Only</SelectItem>
                                <SelectItem value="drop">Drop Only</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {formData.transport_stop_id && (
                          <div>
                            <Label>Payment Plan</Label>
                            <Select value={formData.transport_payment_plan} onValueChange={v => handleInputChange('transport_payment_plan', v)}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="monthly">Monthly (No discount)</SelectItem>
                                <SelectItem value="yearly">Full Year (5% discount)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {/* Transport Fee Preview */}
                        {transportFeePreview && (
                          <div className="p-3 bg-blue-50 rounded-lg text-sm">
                            <p className="font-medium text-blue-800">Transport Fee Preview:</p>
                            <p>Stop: {transportFeePreview.stopName}</p>
                            <p>Distance: {transportFeePreview.distance} km</p>
                            {formData.transport_payment_plan === 'yearly' ? (
                              <>
                                <p>Annual Fee: ₹{transportFeePreview.monthlyFee} × 12 = ₹{transportFeePreview.monthlyFee * 12}</p>
                                <p className="text-green-600">Discount (5%): - ₹{Math.round(transportFeePreview.monthlyFee * 12 * 0.05)}</p>
                                <p className="font-bold text-blue-700">Total Payable: ₹{Math.round(transportFeePreview.monthlyFee * 12 * 0.95)}</p>
                              </>
                            ) : (
                              <p className="font-bold text-blue-700">Monthly Fee: ₹{transportFeePreview.monthlyFee}</p>
                            )}
                          </div>
                        )}
                        
                        <div>
                          <Label>Pickup Point (Optional - for reference)</Label>
                          <Input value={formData.pickup_point} onChange={e => handleInputChange('pickup_point', e.target.value)} placeholder="Landmark near bus stop" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Hostel */}
                  <div className="p-4 border rounded-lg space-y-4">
                    <h3 className="font-semibold flex items-center gap-2"><Heart className="h-4 w-4" /> Hostel</h3>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={formData.hostel_required} onCheckedChange={v => handleInputChange('hostel_required', v)} />
                      <Label>Hostel Required</Label>
                    </div>
                    {formData.hostel_required && (
                      <div className="space-y-3 pl-4 border-l-2 border-green-200">
                        <div>
                          <Label>Select Building *</Label>
                          <Select value={formData.hostel_building_id} onValueChange={v => handleInputChange('hostel_building_id', v)}>
                            <SelectTrigger><SelectValue placeholder="Select hostel building" /></SelectTrigger>
                            <SelectContent>
                              {hostelBuildings.length === 0 ? (
                                <SelectItem value="none" disabled>No hostel buildings available</SelectItem>
                              ) : (
                                hostelBuildings.map(b => (
                                  <SelectItem key={b.id} value={b.id}>{b.name} ({b.building_type || b.hostel_type})</SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {formData.hostel_building_id && (
                          <div>
                            <Label>Select Room *</Label>
                            <Select value={formData.hostel_room_id} onValueChange={v => handleInputChange('hostel_room_id', v)}>
                              <SelectTrigger><SelectValue placeholder="Select room" /></SelectTrigger>
                              <SelectContent>
                                {hostelRooms.length === 0 ? (
                                  <SelectItem value="none" disabled>No rooms available in this building</SelectItem>
                                ) : (
                                  hostelRooms.map(r => (
                                    <SelectItem key={r.id} value={r.id}>
                                      Room {r.room_number} ({r.room_type}) - {r.available_beds} beds free - ₹{r.monthly_fee}/month
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {formData.hostel_room_id && availableBeds.length > 0 && (
                          <div>
                            <Label>Select Bed Number *</Label>
                            <Select value={formData.hostel_bed_number} onValueChange={v => handleInputChange('hostel_bed_number', v)}>
                              <SelectTrigger><SelectValue placeholder="Select bed" /></SelectTrigger>
                              <SelectContent>
                                {availableBeds.map(bed => (
                                  <SelectItem key={bed} value={String(bed)}>Bed {bed}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {formData.hostel_room_id && (
                          <div>
                            <Label>Dietary Preference</Label>
                            <Select value={formData.mess_type} onValueChange={v => handleInputChange('mess_type', v)}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="veg">Vegetarian</SelectItem>
                                <SelectItem value="non_veg">Non-Vegetarian</SelectItem>
                                <SelectItem value="special">Special Diet</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500 mt-1">Mess included in room package</p>
                          </div>
                        )}
                        
                        {formData.hostel_room_id && (
                          <div>
                            <Label>Payment Plan</Label>
                            <Select value={formData.hostel_payment_plan} onValueChange={v => handleInputChange('hostel_payment_plan', v)}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="monthly">Monthly (No discount)</SelectItem>
                                <SelectItem value="yearly">Full Year (5% discount)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {/* Hostel Fee Preview */}
                        {hostelFeePreview && (
                          <div className="p-3 bg-green-50 rounded-lg text-sm">
                            <p className="font-medium text-green-800">Hostel Fee Preview:</p>
                            <p>Room: {hostelFeePreview.roomNumber} ({hostelFeePreview.roomType})</p>
                            <p className="text-xs text-gray-600">* Mess included in room package</p>
                            {formData.hostel_payment_plan === 'yearly' ? (
                              <>
                                <p className="mt-1">Package Fee: ₹{hostelFeePreview.roomFee} × 12 = ₹{hostelFeePreview.roomFee * 12}</p>
                                <p className="text-green-600">Discount (5%): - ₹{Math.round(hostelFeePreview.roomFee * 12 * 0.05)}</p>
                                <p className="border-t pt-1 mt-1">Security Deposit ({hostelFeePreview.depositMonths || 2} months): ₹{hostelFeePreview.securityDeposit}</p>
                                <p className="font-bold text-green-700">Total Payable: ₹{Math.round(hostelFeePreview.roomFee * 12 * 0.95) + hostelFeePreview.securityDeposit}</p>
                              </>
                            ) : (
                              <>
                                <p className="mt-1">Package Fee: ₹{hostelFeePreview.roomFee}/month</p>
                                <p className="border-t pt-1 mt-1">Security Deposit ({hostelFeePreview.depositMonths || 2} months): ₹{hostelFeePreview.securityDeposit}</p>
                                <p className="font-bold text-green-700">First Month Total: ₹{hostelFeePreview.roomFee + hostelFeePreview.securityDeposit}</p>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Fee Category */}
                <div className="p-4 border rounded-lg space-y-4">
                  <h3 className="font-semibold flex items-center gap-2"><CreditCard className="h-4 w-4" /> Fee Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Fee Category</Label>
                      <Select value={formData.fee_category} onValueChange={v => handleInputChange('fee_category', v)}>
                        <SelectTrigger><SelectValue placeholder="Select fee category" /></SelectTrigger>
                        <SelectContent>
                          {feeCategories.length > 0 ? (
                            feeCategories.map(cat => (
                              <SelectItem key={cat.id} value={cat.code}>
                                {cat.name} {cat.discount_percentage > 0 ? `(${cat.discount_percentage}% discount)` : ''}
                              </SelectItem>
                            ))
                          ) : (
                            <>
                              <SelectItem value="general">General</SelectItem>
                              <SelectItem value="staff_ward">Staff Ward</SelectItem>
                              <SelectItem value="sibling">Sibling Discount</SelectItem>
                              <SelectItem value="scholarship">Scholarship</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                      {feeCategories.length > 0 && formData.fee_category && (
                        <p className="text-xs text-green-600 mt-1">
                          {(() => {
                            const selected = feeCategories.find(c => c.code === formData.fee_category);
                            return selected?.discount_percentage > 0 
                              ? `✓ ${selected.discount_percentage}% discount will be auto-applied on fees`
                              : '';
                          })()}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <Checkbox checked={formData.scholarship_applied} onCheckedChange={v => handleInputChange('scholarship_applied', v)} />
                      <Label>Additional Scholarship</Label>
                    </div>
                  </div>
                  {formData.scholarship_applied && (
                    <div>
                      <Label>Scholarship Type</Label>
                      <Input value={formData.scholarship_type} onChange={e => handleInputChange('scholarship_type', e.target.value)} placeholder="e.g., Merit, SC/ST, EWS" />
                    </div>
                  )}
                </div>

                {/* Fee Summary Preview */}
                {(transportFeePreview || hostelFeePreview) && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h3 className="font-semibold text-amber-800 mb-2">💰 Total Fee Summary (First Month)</h3>
                    <div className="grid md:grid-cols-2 gap-2 text-sm">
                      <p>Admission Fee: As per class structure</p>
                      <p>Tuition Fee: As per class structure</p>
                      {transportFeePreview && (
                        <p>Transport Fee: ₹{transportFeePreview.monthlyFee}</p>
                      )}
                      {hostelFeePreview && (
                        <>
                          <p>Hostel Fee: ₹{hostelFeePreview.roomFee}</p>
                          <p>Mess Fee: ₹{hostelFeePreview.messFee}</p>
                          <p>Security Deposit: ₹{hostelFeePreview.securityDeposit}</p>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-amber-700 mt-2">* Exact fees will be calculated based on class fee structure</p>
                  </div>
                )}

                {/* Save Button */}
                <div className="flex justify-center pt-4">
                  <Button size="lg" onClick={handleSaveStudent} disabled={loading || (!editMode && savedStudentId)}>
                    {loading ? 'Saving...' : editMode ? 'Update Student' : (savedStudentId ? '✓ Student Saved' : 'Save Student & Continue to Documents')}
                  </Button>
                </div>
              </TabsContent>

              {/* Documents */}
              <TabsContent value="documents" className="space-y-4">
                {!savedStudentId ? (
                  <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
                    <p className="text-muted-foreground">Please save student details first before uploading documents</p>
                    <Button className="mt-4" onClick={() => setActiveTab('other')}>Go to Save</Button>
                  </div>
                ) : (
                  <>
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg mb-4">
                      <p className="text-green-800 dark:text-green-400 flex items-center gap-2">
                        <CheckCircle className="h-5 w-5" />
                        Student saved! Now upload required documents.
                      </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      {DOCUMENT_TYPES.map(doc => (
                        <div key={doc.key} className={`p-4 border rounded-lg ${doc.required ? 'border-amber-300' : ''}`}>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="flex items-center gap-2">
                              {doc.label}
                              {doc.required && <Badge variant="outline" className="text-xs">Required</Badge>}
                            </Label>
                            {uploadedDocs[doc.key] && <CheckCircle className="h-4 w-4 text-green-500" />}
                          </div>
                          <div className="flex gap-2">
                            <Input
                              type="file"
                              accept={doc.accept}
                              className="text-sm"
                              onChange={e => e.target.files?.[0] && handleDocumentUpload(doc.key, e.target.files[0])}
                              disabled={uploadingDoc === doc.key}
                            />
                          </div>
                          {uploadingDoc === doc.key && <p className="text-xs text-muted-foreground mt-1">Uploading...</p>}
                          {uploadedDocs[doc.key] && <p className="text-xs text-green-600 mt-1">✓ Uploaded</p>}
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-center pt-6">
                      <Button size="lg" onClick={() => navigate('/students')}>
                        <CheckCircle className="h-4 w-4 mr-2" /> Complete Admission
                      </Button>
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  const idx = tabs.findIndex(t => t.id === activeTab);
                  if (idx > 0) setActiveTab(tabs[idx - 1].id);
                }}
                disabled={activeTab === 'basic'}
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Previous
              </Button>
              <Button
                onClick={() => {
                  const idx = tabs.findIndex(t => t.id === activeTab);
                  if (idx < tabs.length - 1) setActiveTab(tabs[idx + 1].id);
                }}
                disabled={activeTab === 'documents'}
              >
                Next <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdmissionFormPage;
